import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { api } from '../shared/api/api-client.ts'
import { adminSessionQueryKey } from '../modules/admin/admin-session.ts'

const navigationGroups = [
  {
    id: 'conteudo',
    label: 'Conteúdo',
    links: [
      { to: '/admin/momentos', label: 'Momentos', mark: 'M' },
      { to: '/admin/edicoes', label: 'Edições', mark: 'E' },
      { to: '/admin/acervo', label: 'Assets e Experiências', mark: 'A' },
      { to: '/admin/site', label: 'Site e SEO', mark: 'S' },
    ],
  },
  {
    id: 'comercial',
    label: 'Comercial',
    links: [
      { to: '/admin/ofertas', label: 'Ofertas', mark: 'O' },
      { to: '/admin/cupons', label: 'Cupons', mark: 'C' },
    ],
  },
  {
    id: 'relacionamento',
    label: 'Relacionamento',
    links: [{ to: '/admin/newsletter', label: 'Newsletter', mark: 'N' }],
  },
  {
    id: 'operacao',
    label: 'Operação',
    links: [
      { to: '/admin/operacao', label: 'Operação e suporte', mark: 'O' },
      { to: '/admin/migracao', label: 'Migração do acervo', mark: 'M' },
      { to: '/admin/saude', label: 'Saúde e relatórios', mark: 'S' },
    ],
  },
] as const

function AdminMenu({ idPrefix }: { idPrefix: 'desktop' | 'mobile' }) {
  return (
    <nav className="admin-menu" aria-label="Áreas da administração">
      {navigationGroups.map((group) => {
        const headingId = `admin-menu-${idPrefix}-${group.id}`
        return (
          <section key={group.id} className="admin-menu__group" aria-labelledby={headingId}>
            <h2 id={headingId}>{group.label}</h2>
            <ul>
              {group.links.map((link) => (
                <li key={link.to}>
                  <NavLink to={link.to}>
                    <span aria-hidden="true">{link.mark}</span>
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </nav>
  )
}

function AdminSession() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [leaving, setLeaving] = useState(false)
  const session = useQuery({
    queryKey: adminSessionQueryKey,
    queryFn: api.getAdminSession,
    staleTime: 4 * 60_000,
  })

  const leave = async () => {
    setLeaving(true)
    try {
      await api.deleteAdminSession()
    } finally {
      queryClient.removeQueries({ queryKey: ['admin'] })
      void navigate('/admin/entrar', { replace: true })
    }
  }

  return (
    <div className="admin-layout__session">
      <span>Conectado como</span>
      <strong translate="no">@{session.data?.admin.username ?? 'admin'}</strong>
      <button type="button" onClick={() => void leave()} disabled={leaving}>
        {leaving ? 'Saindo…' : 'Sair'}
      </button>
    </div>
  )
}

export function AdminLayout() {
  const location = useLocation()
  const content = useRef<HTMLElement>(null)
  const previousPath = useRef(location.pathname)

  useEffect(() => {
    if (previousPath.current === location.pathname) return
    const pathBeforeNavigation = previousPath.current
    previousPath.current = location.pathname
    if (pathBeforeNavigation !== '/admin') content.current?.focus()
  }, [location.pathname])

  return (
    <div className="admin-layout">
      <meta name="robots" content="noindex,nofollow" />
      <a className="skip-link" href="#admin-content">
        Pular para o conteúdo do painel
      </a>

      <aside className="admin-layout__sidebar">
        <div className="admin-layout__brand">
          <span aria-hidden="true">
            <img src="/brand/tintin-logo-face.png" alt="" width="46" height="46" />
          </span>
          <div>
            <strong>Momentos com Tintin</strong>
            <small>Mesa editorial</small>
          </div>
        </div>
        <NavLink className="admin-layout__site-link" to="/">
          Ver site <span aria-hidden="true">↗</span>
        </NavLink>
        <AdminMenu idPrefix="desktop" />
        <AdminSession />
      </aside>

      <header className="admin-layout__mobile-header">
        <NavLink to="/admin/momentos" aria-label="Administração — início">
          <img src="/brand/tintin-logo-face.png" alt="" width="42" height="42" />
          <span>
            <strong>Mesa editorial</strong>
            <small>Administração</small>
          </span>
        </NavLink>
        <details key={location.pathname} className="admin-mobile-menu">
          <summary>Menu do painel</summary>
          <div>
            <NavLink className="admin-layout__site-link" to="/">
              Ver site <span aria-hidden="true">↗</span>
            </NavLink>
            <AdminMenu idPrefix="mobile" />
            <AdminSession />
          </div>
        </details>
      </header>

      <main ref={content} id="admin-content" className="admin-layout__content" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}
